import { MessageSquare, Send, User, Clock, Reply, MoreHorizontal, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocumentComments, useAddComment, type KBComment } from '@/hooks/knowledge'
import { cn } from '@/lib/utils'

interface CommentSectionProps {
  documentId: string
  className?: string
}

/**
 * 评论区组件
 * 显示文档评论列表，支持添加评论和回复
 */
export function CommentSection({ documentId, className }: CommentSectionProps) {
  const { data: comments, isLoading } = useDocumentComments(documentId)
  const addComment = useAddComment()
  const [newComment, setNewComment] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim()) { return }

    addComment.mutate(
      {
        documentId,
        content: newComment.trim(),
        parentId: replyTo ?? undefined,
      },
      {
        onSuccess: () => {
          setNewComment('')
          setReplyTo(null)
        },
      }
    )
  }

  // 构建评论树结构
  const buildCommentTree = (comments: KBComment[]): CommentWithReplies[] => {
    const map = new Map<string, CommentWithReplies>()
    const roots: CommentWithReplies[] = []

    // 初始化所有评论
    comments.forEach((comment) => {
      map.set(comment.id, { ...comment, replies: [] })
    })

    // 构建树
    comments.forEach((comment) => {
      const node = map.get(comment.id)
      if (!node) { return }

      if (comment.parentId && map.has(comment.parentId)) {
        map.get(comment.parentId)?.replies.push(node)
      } else {
        roots.push(node)
      }
    })

    return roots
  }

  if (isLoading) {
    return <CommentSectionSkeleton />
  }

  const commentTree = comments ? buildCommentTree(comments) : []

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Comment Input */}
      <div className="border-b p-4">
        <form onSubmit={handleSubmit}>
          {replyTo && (
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Reply className="h-4 w-4" />
              <span>回复评论</span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                className="text-primary hover:underline"
              >
                取消
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="写下你的评论..."
              className="flex-1 min-h-[80px] rounded-md border bg-transparent px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
            />
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={!newComment.trim() || addComment.isPending}
            >
              <Send className="mr-2 h-4 w-4" />
              发送
            </Button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-auto">
        {commentTree.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">暂无评论</p>
            <p className="text-sm mt-1">成为第一个评论的人</p>
          </div>
        ) : (
          <div className="divide-y">
            {commentTree.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={(id) => setReplyTo(id)}
                depth={0}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== Types ====================

interface CommentWithReplies extends KBComment {
  replies: CommentWithReplies[]
}

// ==================== Sub Components ====================

interface CommentItemProps {
  comment: CommentWithReplies
  onReply: (id: string) => void
  depth: number
}

function CommentItem({ comment, onReply, depth }: CommentItemProps) {
  const maxDepth = 3 // 最大嵌套深度

  return (
    <div className={cn('py-4', depth > 0 && 'pl-8 border-l ml-4')}>
      <div className="px-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              {comment.author?.avatar ? (
                <img
                  src={comment.author.avatar}
                  alt={comment.author.name}
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div>
              <span className="font-medium text-sm">
                {comment.author?.name ?? '未知用户'}
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(comment.createdAt)}
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        <div className="mt-2 text-sm whitespace-pre-wrap">{comment.content}</div>

        {/* Actions */}
        {depth < maxDepth && (
          <div className="mt-2">
            <button
              onClick={() => onReply(comment.id)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Reply className="h-3 w-3" />
              回复
            </button>
          </div>
        )}
      </div>

      {/* Replies */}
      {comment.replies.length > 0 && (
        <div className="mt-2">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReply={onReply}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CommentSectionSkeleton() {
  return (
    <div className="flex flex-col h-full">
      <div className="border-b p-4">
        <Skeleton className="h-20 w-full" />
        <div className="mt-2 flex justify-end">
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
      <div className="flex-1 divide-y">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16 mt-1" />
              </div>
            </div>
            <Skeleton className="h-4 w-full mt-2" />
            <Skeleton className="h-4 w-3/4 mt-1" />
          </div>
        ))}
      </div>
    </div>
  )
}

// ==================== Helpers ====================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return '刚刚'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} 分钟前`
  } else if (diffHours < 24) {
    return `${diffHours} 小时前`
  } else if (diffDays < 7) {
    return `${diffDays} 天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}
